import { useState, useCallback, useRef, useEffect } from 'react';
import {
  downloadFilesAsZip,
  type DownloadableFile,
  type DownloadProgress,
  type DownloadResult,
} from 'src/lib/utils/download-utils';
import { multiDownload } from 'src/lib/utils/multi-download';
import { useToast } from 'src/components/ui/use-toast';
import { useTranslation } from 'react-i18next';

export interface UseDownloadFilesOptions {
  /** Function to collect files to download */
  getFiles: () => DownloadableFile[];
  /**
   * How many files were left out because the antivirus scan is holding them
   * (they have no URL yet). Reported in the result toast so a partial download
   * never reads as a complete one.
   */
  countHeld?: () => number;
  /** Name for the ZIP file (without .zip extension) */
  zipFileName?: string;
}

export const useDownloadFiles = ({
  getFiles,
  countHeld,
  zipFileName = 'files',
}: UseDownloadFilesOptions) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const heldNote = useCallback(
    (held: number) =>
      t('actions:download_scan_held_note', {
        defaultValue: `${held} file(s) are still being scanned for viruses or were blocked, and weren't included.`,
        count: held,
      }),
    [t],
  );

  /** Every result toast goes through this, so a partial download never reads as complete. */
  const withHeldNote = useCallback(
    (message: string, held: number): string =>
      held > 0 ? `${message} ${heldNote(held)}` : message,
    [heldNote],
  );

  /** No URLs at all: say whether that's the scan or genuinely nothing to download. */
  const toastNoFiles = useCallback(
    (held: number) => {
      toast({
        variant: 'destructive',
        description:
          held > 0
            ? heldNote(held)
            : t('actions:no_files_description', {
                defaultValue: 'No downloadable files found.',
              }),
      });
    },
    [toast, t, heldNote],
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleDownloadAsZip = useCallback(async () => {
    setIsDownloading(true);
    setDownloadProgress(null);

    // Create new AbortController for this download
    abortControllerRef.current = new AbortController();

    try {
      const files = getFiles();
      const held = countHeld?.() ?? 0;

      if (files.length === 0) {
        toastNoFiles(held);
        return;
      }

      const result = await downloadFilesAsZip(
        files,
        `${zipFileName}.zip`,
        (progress: DownloadProgress) => {
          setDownloadProgress(progress);
        },
        abortControllerRef.current.signal,
      );

      // Show appropriate toast based on result
      if (result.failedCount === 0) {
        toast({
          variant: held > 0 ? 'warning' : 'success',
          description: withHeldNote(
            t('actions:client_download_success', {
              defaultValue: `Successfully downloaded ${files.length} files.`,
            }),
            held,
          ),
        });
      } else if (result.successCount > 0) {
        toast({
          variant: 'warning',
          description: withHeldNote(
            t('actions:client_download_partial', {
              defaultValue: `Downloaded ${result.successCount}/${files.length} files. ${
                result.failedCount
              } files failed: ${result.failedFiles.join(', ')}`,
            }),
            held,
          ),
        });
      }
    } catch (error) {
      // Handle cancellation gracefully
      if (error instanceof Error && error.name === 'AbortError') {
        toast({
          variant: 'info',
          description: t('actions:download_cancelled', {
            defaultValue: 'Download cancelled.',
          }),
        });
        return;
      }

      console.error('Download failed:', error);
      toast({
        variant: 'destructive',
        description: t('actions:download_error_description', {
          defaultValue: 'Failed to download files. Please try again.',
        }),
      });
    } finally {
      setIsDownloading(false);
      setDownloadProgress(null);
      abortControllerRef.current = null;
    }
  }, [getFiles, countHeld, zipFileName, toast, t, withHeldNote, toastNoFiles]);

  // Auto-cancel downloads when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleDownloadOneByOne = useCallback(async () => {
    const files = getFiles();
    const held = countHeld?.() ?? 0;

    if (files.length === 0) {
      toastNoFiles(held);
      return;
    }

    setIsDownloading(true);

    try {
      const result = await multiDownload(files.map((f) => ({ url: f.url, filename: f.filename })));

      if (result.failedFiles.length === 0) {
        toast({
          variant: held > 0 ? 'warning' : 'success',
          description: withHeldNote(
            t('actions:download_started', {
              defaultValue: `Started downloading ${result.successCount} files.`,
            }),
            held,
          ),
        });
      } else if (result.successCount > 0) {
        toast({
          variant: 'destructive',
          title: t('actions:download_partial_title', {
            defaultValue: 'Some files could not be downloaded',
          })!,
          description: withHeldNote(
            t('actions:download_partial_description', {
              defaultValue: `${
                result.failedFiles.length
              } file(s) may have been removed or are no longer available: ${result.failedFiles.join(
                ', ',
              )}`,
            }),
            held,
          ),
        });
      } else {
        toast({
          variant: 'destructive',
          title: t('actions:download_all_failed_title', {
            defaultValue: 'Download failed',
          })!,
          description: withHeldNote(
            t('actions:download_all_failed_description', {
              defaultValue:
                'Files may have been removed or are no longer available. Please contact support if this issue persists.',
            }),
            held,
          ),
        });
      }
    } catch (error) {
      console.error('Download one-by-one failed:', error);
      toast({
        variant: 'destructive',
        description: t('actions:download_error_description', {
          defaultValue: 'Failed to download files. Please try again.',
        }),
      });
    } finally {
      setIsDownloading(false);
    }
  }, [getFiles, countHeld, toast, t, withHeldNote, toastNoFiles]);

  return {
    isDownloading,
    downloadProgress,
    handleDownloadAsZip,
    handleDownloadOneByOne,
  };
};
